/**
 * Simulation runtime and its React binding.
 *
 * The simulation owns the world; React only ever *reads* it to render controls
 * and statistics. Nothing in `src/simulation` knows React exists, and nothing
 * here reaches into the state arrays except to hand them to the renderer. That
 * separation is what lets the same engine drive the GPU backend, the tests and
 * the headless tuning scripts unchanged.
 *
 * State changes are published through `useSyncExternalStore` rather than
 * `useState`, so a 60 Hz simulation does not cause 60 React renders per second:
 * only the components that actually read the changed slice re-render.
 */

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';

import { CpuBackend } from '../simulation/cpuBackend.ts';
import type { BrushMode, BrushStroke, SimBackend, SimStats } from '../simulation/types.ts';
import { cloneParams, defaultParamsFor, type SimParams, type TerrainPreset } from '../config.ts';
import { defaultCamera, type CameraState } from '../rendering/camera.ts';
import { DEFAULT_INTERNAL_WIDTH } from '../rendering/softwareTerrain.ts';
import type { ViewMode } from '../rendering/viewModes.ts';

export interface RuntimeStats extends SimStats {
  /** Milliseconds the last render took. */
  renderTimeMs: number;
}

export interface SimulationStore {
  subscribe(fn: () => void): () => void;
  getParams(): SimParams;
  getStats(): RuntimeStats;
  getCamera(): CameraState;
  getRunning(): boolean;
  getMode(): ViewMode;
  getBrush(): BrushSettings;
  getBackend(): SimBackend;
  /** Notify React that a mutable slice changed. */
  touch(): void;
}

export interface BrushSettings {
  mode: BrushMode;
  radius: number;
  strength: number;
}

const EMPTY_STATS: RuntimeStats = {
  tick: 0,
  simTime: 0,
  fps: 0,
  frameTimeMs: 0,
  simTimeMs: 0,
  renderTimeMs: 0,
  waterTotal: 0,
  sedimentTotal: 0,
  looseTotal: 0,
  terrainSum: 0,
  meanHeight: 0,
  minHeight: 0,
  maxHeight: 0,
  peakHeight: 0,
  windLossTotal: 0,
  gridWidth: 0,
  gridHeight: 0,
  backend: '',
};

/**
 * Owns one `CpuBackend` plus all the UI-level state that surrounds it.
 *
 * Note the deliberate asymmetry: the *simulation* parameters live in a plain
 * mutable object (the UI edits it in place, exactly like a native instrument
 * panel), while view state (camera, display mode, playback) is published through
 * the store. Copying the parameter block on every slider drag would allocate
 * per frame for no benefit.
 */
export class SimulationRuntime implements SimulationStore {
  readonly params: SimParams;
  backend: SimBackend;
  camera: CameraState = defaultCamera();
  mode: ViewMode = 'terrain';
  running = false;
  brush: BrushSettings = { mode: 'raise', radius: 0.06, strength: 12 };
  exposure = 1;
  gridStep = 0;
  showWater = true;
  /**
   * Internal render raster width for the CPU viewport.
   *
   * A renderer setting rather than a simulation one, but it lives here because it
   * is view state that the Info panel edits.
   *
   * It is **manual on purpose**. An earlier version moved this automatically to hold
   * a frame-time target, and every move flickered: the canvas is upscaled by the
   * browser, so changing the raster width changes the upscale factor and the whole
   * image visibly re-scales. Trading a stable picture for a frame-rate target is the
   * wrong trade for a visual instrument, so the quality/speed choice is the user's.
   */
  renderWidth = DEFAULT_INTERNAL_WIDTH;
  /** Frames-per-second of the render loop, smoothed. */
  fps = 60;
  /** What the backend select is set to, which may differ from what is running. */
  requestedBackend: BackendChoice = 'auto';
  /** Adapter description once a GPU backend is live, else empty. */
  adapterName = '';
  /** Human-readable reason the requested accelerator could not be activated. */
  backendError = '';
  private listeners = new Set<() => void>();
  private stats: RuntimeStats = { ...EMPTY_STATS };
  private version = 0;

  constructor(preset: TerrainPreset = 'mountains') {
    this.params = defaultParamsFor(preset);
    this.backend = new CpuBackend(this.params);
    this.stats = { ...this.stats, gridWidth: this.backend.width, gridHeight: this.backend.height };
    this.publish();
  }

  /* ---- store plumbing --------------------------------------------------- */

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): number => this.version;

  touch = (): void => this.publish();

  private publish(): void {
    this.version++;
    for (const fn of this.listeners) fn();
  }

  getParams = (): SimParams => this.params;
  getStats = (): RuntimeStats => this.stats;
  getCamera = (): CameraState => this.camera;
  getRunning = (): boolean => this.running;
  getMode = (): ViewMode => this.mode;
  getBrush = (): BrushSettings => this.brush;
  getBackend = (): SimBackend => this.backend;

  setStats(stats: RuntimeStats): void {
    this.stats = stats;
    this.publish();
  }

  /* ---- actions ---------------------------------------------------------- */

  /**
   * Rebuild the backend. Needed when the grid resolution changes, because the
   * state arrays are sized from it.
   */
  private rebuildBackend(): void {
    if (this.backend.width === this.params.terrain.resolution) {
      this.backend.setParams(this.params);
      return;
    }
    this.backend.destroy();
    this.backend = new CpuBackend(this.params);
    this.publish();
  }

  /** Restore the terrain exactly as generated from the current seed. */
  resetSimulation(): void {
    this.backend.resetToInitial();
    this.touch();
  }

  /** Generate a fresh terrain from the current seed and parameters. */
  regenerate(): void {
    this.backend.setParams(this.params);
    this.backend.regenerate();
    this.touch();
  }

  /** Switch preset, applying that preset's tuned defaults. */
  setPreset(preset: TerrainPreset): void {
    const next = defaultParamsFor(preset);
    // Keep the grid and time settings the user chose; adopt the shape presets.
    next.terrain.resolution = this.params.terrain.resolution;
    next.terrain.cellSize = this.params.terrain.cellSize;
    next.terrain.seed = this.params.terrain.seed;
    next.engine = { ...this.params.engine };
    Object.assign(this.params.terrain, next.terrain);
    this.rebuildBackend();
    this.backend.regenerate();
    this.touch();
  }

  /** Apply a parameter mutation and push it to the engine. */
  mutate(fn: (p: SimParams) => void): void {
    fn(this.params);
    this.backend.setParams(this.params);
    this.touch();
  }

  /** Change the grid resolution (rebuilds the world). */
  setResolution(resolution: number): void {
    this.params.terrain.resolution = resolution as SimParams['terrain']['resolution'];
    this.rebuildBackend();
    this.backend.regenerate();
    this.touch();
  }

  setSeed(seed: number): void {
    this.params.terrain.seed = seed;
    this.backend.setParams(this.params);
    this.backend.regenerate();
    this.touch();
  }

  toggleRunning(): void {
    this.running = !this.running;
    this.touch();
  }

  setRunning(value: boolean): void {
    this.running = value;
    this.touch();
  }

  step(): void {
    this.backend.tick(this.params.engine.iterationsPerFrame);
    this.touch();
  }

  applyBrush(stroke: BrushStroke): void {
    this.backend.applyBrush(stroke);
    this.touch();
  }

  /** Snapshot of the packed state, for export. */
  async readState(): Promise<Float32Array> {
    return this.backend.readState();
  }

  /** Replace the world; used by heightmap import. */
  writeState(data: Float32Array): void {
    this.backend.writeState(data);
    this.touch();
  }

  /** Copy of the current parameters, for persistence. */
  snapshotParams(): SimParams {
    return cloneParams(this.params);
  }

  restoreParams(next: SimParams): void {
    Object.assign(this.params.terrain, next.terrain);
    Object.assign(this.params.hydraulic, next.hydraulic);
    Object.assign(this.params.gravity, next.gravity);
    Object.assign(this.params.wind, next.wind);
    Object.assign(this.params.engine, next.engine);
    this.rebuildBackend();
    this.backend.regenerate();
    this.touch();
  }

  getMaxHeight(): number {
    return Math.max(1, this.stats.maxHeight || this.params.terrain.height);
  }
}

/* -------------------------------------------------------------------------- */
/* React binding                                                              */
/* -------------------------------------------------------------------------- */

export function useRuntimeVersion(store: SimulationRuntime): number {
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}

/**
 * The single runtime instance for the application.
 *
 * Held in a module-level ref rather than a React state slot so that a hot reload
 * or a StrictMode double-mount cannot construct a second simulator (which would
 * double the memory and halve the framerate in a very confusing way).
 */
let runtimeSingleton: SimulationRuntime | null = null;
export function getRuntime(): SimulationRuntime {
  if (!runtimeSingleton) runtimeSingleton = new SimulationRuntime();
  return runtimeSingleton;
}

/**
 * Which backend the user asked for.
 *
 * `auto` prefers WebGPU and falls back to the CPU without interrupting the app.
 * An explicit `gpu` choice keeps the same fallback but exposes the reason in the
 * Info panel so an unavailable adapter is diagnosable rather than a frozen world.
 */
export type BackendChoice = 'auto' | 'gpu' | 'cpu';

/**
 * Apply whatever backend the user has asked for. Safe to call more than once.
 *
 * Called on mount so a persisted choice takes effect, and whenever the backend
 * select changes. `auto`, the default, probes WebGPU and falls back to the CPU.
 */
export async function applyRequestedBackend(runtime: SimulationRuntime): Promise<'gpu' | 'cpu'> {
  return setBackend(runtime, runtime.requestedBackend);
}

/**
 * Switch backends, carrying the current world across.
 *
 * Reversible on purpose. Having a switch means the fallback path is exercised by
 * the same code the primary path uses, so it cannot quietly rot — and it lets the
 * two implementations be compared on the same terrain, which is the only honest
 * way to check that the WGSL transcription of `kernels.ts` agrees with it.
 */
export async function setBackend(runtime: SimulationRuntime, choice: BackendChoice): Promise<'gpu' | 'cpu'> {
  runtime.requestedBackend = choice;
  const wantGpu = choice !== 'cpu';
  if (!wantGpu) {
    if (runtime.backend.kind !== 'cpu') {
      const previous = runtime.backend;
      const carried = await previous.readState();
      const next = new CpuBackend(runtime.params);
      carryState(next, carried);
      runtime.backend = next;
      previous.destroy();
    }
    runtime.adapterName = '';
    runtime.backendError = '';
    runtime.touch();
    return 'cpu';
  }

  try {
    const { GpuBackend } = await import('../gpu/gpuBackend.ts');
    const created = await GpuBackend.create(runtime.params);
    if (!created) {
      // No adapter, or a device that cannot bind the state buffer. Only an
      // explicit request is worth surfacing; `auto` is expected to fall back.
      runtime.adapterName = '';
      runtime.backendError =
        typeof navigator !== 'undefined' && navigator.gpu
          ? 'WebGPU 设备或所需缓冲区不可用'
          : '当前浏览器未提供 WebGPU';
      runtime.touch();
      return 'cpu';
    }
    const previous = runtime.backend;
    const carried = await previous.readState();
    carryState(created.backend, carried);
    runtime.backend = created.backend;
    previous.destroy();
    runtime.adapterName = created.adapterInfo;
    runtime.backendError = '';
    runtime.touch();
    return 'gpu';
  } catch (error) {
    runtime.adapterName = '';
    runtime.backendError = error instanceof Error ? error.message : String(error);
    console.error('[mountain-lab] GPU backend activation failed', error);
    runtime.touch();
    return 'cpu';
  }
}

/** Hand the current terrain to a freshly built backend, if the sizes agree. */
function carryState(target: SimBackend, state: Float32Array): void {
  try {
    target.writeState(state);
  } catch {
    /* grid sizes differ; the freshly generated terrain is the right answer */
  }
}

export interface AnimationLoopOptions {
  runtime: SimulationRuntime;
  /** Called once per rendered frame with the state and grid width. */
  onFrame: (state: Float32Array, width: number, dt: number) => void;
}

/**
 * Drive the simulation and the renderer from one requestAnimationFrame loop.
 *
 * Simulation and rendering are decoupled on purpose: a running simulation renders
 * every frame, while a paused and unchanged viewport keeps the canvas it already
 * has. Any camera, display or state action publishes a new runtime version and
 * therefore schedules a fresh paused frame. This avoids spending a full CPU core
 * redrawing an identical image while the user is reading the controls.
 *
 * The time multiplier is realised as *extra ticks per frame* rather than by
 * scaling a wall-clock interval — a scheduler that hands out a variable number
 * of ticks is the only thing that stays faithful when the grid is too big to tick
 * every frame, which is exactly the case the architecture is built for.
 *
 * The accumulator is fed by real elapsed time, so the same settings produce the
 * same simulation rate on a 60 Hz and a 144 Hz display.
 */
export function useAnimationLoop({ runtime, onFrame }: AnimationLoopOptions): void {
  const frame = useRef<number>(0);
  const last = useRef<number>(0);
  const acc = useRef<number>(0);

  useEffect(() => {
    let alive = true;
    let renderedVersion = -1;
    /** One tick per this many milliseconds of wall clock at 1x. */
    const PERIOD_MS = 1000 / 60;
    /**
     * Soft ceiling on how much of a frame may go to simulation.
     *
     * On a grid too large to tick every frame the honest behaviour is to tick
     * *less often* and keep the viewport responsive, rather than to spend the
     * whole frame in the simulation and stall the UI. The budget below is what
     * makes `iterationsPerFrame` an upper bound instead of a promise.
     */
    const SIM_BUDGET_MS = 24;

    const tick = (now: number): void => {
      if (!alive) return;
      const dt = last.current === 0 ? PERIOD_MS : Math.min(250, now - last.current);
      last.current = now;
      runtime.fps = runtime.fps * 0.9 + (1000 / Math.max(1, dt)) * 0.1;

      if (runtime.running) {
        acc.current += dt * runtime.params.engine.timeScale;
        const cap = runtime.params.engine.iterationsPerFrame;
        let steps = acc.current >= PERIOD_MS ? Math.min(cap, Math.floor(acc.current / PERIOD_MS)) : 0;
        if (steps > 0) {
          // Measure one tick, then spend at most the budget on the rest of them.
          const t0 = performance.now();
          runtime.backend.tick(1);
          const perTick = performance.now() - t0;
          const affordable = Math.max(0, Math.min(steps - 1, Math.floor(SIM_BUDGET_MS / Math.max(0.05, perTick))));
          if (affordable > 0) runtime.backend.tick(affordable);
          acc.current -= (1 + affordable) * PERIOD_MS;
        }
        // Never let the accumulator grow without bound when the budget cannot
        // keep up: the simulation falls behind real time rather than exploding
        // into a catch-up burst on the next frame.
        const maxAcc = PERIOD_MS * cap * 2;
        if (acc.current > maxAcc) acc.current = maxAcc;
      } else {
        acc.current = 0;
      }

      // A canvas retains its pixels. When paused, redraw only after a published
      // camera/display/state change; the RAF itself stays alive so input is shown
      // on the very next display frame without restarting the loop.
      const version = runtime.getSnapshot();
      if (runtime.running || version !== renderedVersion) {
        onFrame((runtime.backend as CpuBackend).stateView, runtime.backend.width, dt);
        // onFrame may publish sampled diagnostics, so capture the post-frame value.
        renderedVersion = runtime.getSnapshot();
      }
      frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => {
      alive = false;
      cancelAnimationFrame(frame.current);
      last.current = 0;
    };
  }, [runtime, onFrame]);
}

/* -------------------------------------------------------------------------- */
/* Small helpers used by the panels                                           */
/* -------------------------------------------------------------------------- */

/** Debounced value, used so text/number inputs do not regenerate per keystroke. */
export function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/** Stable callback identity for use inside the animation loop. */
export function useEvent<A extends unknown[]>(fn: (...args: A) => void): (...args: A) => void {
  const ref = useRef(fn);
  ref.current = fn;
  return useCallback((...args: A) => ref.current(...args), []);
}

export function useRuntime(): SimulationRuntime {
  return useMemo(() => getRuntime(), []);
}
